<script setup>
// Takip ve olcum: pikseller, veri kumesi kalitesi ve reklam URL etiketleri.
//
// URL etiketi degisikligi yayindaki reklamin tiklama adresini etkiler ve
// raporlamayi bozabilir; bu yuzden yazma isleminde once onay aliniyor.
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';
import { aracCalistir, diziyeCevir } from './kopru.js';
import AracTablosu from './AracTablosu.vue';

const props = defineProps({
  hesap: { type: String, default: '' },
  hesapAyrinti: { type: Object, default: null },
});

const { t } = useI18n();
const route = useRoute();

const hesapKimligi = computed(() => route.params.accountId);

// Meta veri kumesi alanlarini snake_case ("match_rate_approx") ve tarihleri
// ham damga olarak dondurur. Ekranda ham anahtar gormek kullaniciya hicbir sey
// anlatmadigi icin cevirisi olan anahtari Turkce basliga ceviriyoruz; cevirisi
// olmayan anahtar uydurulmadan oldugu gibi kaliyor (vue-i18n karsilik
// bulamayinca anahtarin kendisini dondurur).
const ALAN_ONEKI = 'META_ADS.AYARLAR.TAKIP.ALAN.';

const alanEtiketi = anahtar => {
  const yol = ALAN_ONEKI + anahtar.toUpperCase();
  const metin = t(yol);
  return metin === yol ? anahtar : metin;
};

const TARIH_ALANLARI = [
  'last_fired_time',
  'last_fired_at',
  'created_time',
  'updated_time',
  'last_upload_time',
];

const tarihBicimle = deger => {
  const ham = String(deger);
  // Unix saniyesi 10 hanelidir; cozulemeyen deger oldugu gibi gosterilir.
  const zaman = /^\d{10}$/.test(ham) ? new Date(Number(ham) * 1000) : new Date(ham);
  return Number.isNaN(zaman.getTime())
    ? ham
    : zaman.toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' });
};

// Ic ice nesne/dizi tek satira sigmaz; ham JSON yerine ozet gosteriyoruz.
const icerikOzeti = deger => {
  if (Array.isArray(deger)) {
    return t('META_ADS.AYARLAR.ORTAK.OGE_SAYISI', { sayi: deger.length });
  }
  const tanitici = deger.name || deger.display_string || deger.id;
  if (tanitici) return String(tanitici);
  return t('META_ADS.AYARLAR.ORTAK.ALAN_SAYISI', { sayi: Object.keys(deger).length });
};

const alanDegeri = (anahtar, deger) => {
  if (deger === null || deger === '') return '—';
  if (typeof deger === 'boolean') {
    return deger ? t('META_ADS.AYARLAR.ORTAK.EVET') : t('META_ADS.AYARLAR.ORTAK.HAYIR');
  }
  if (typeof deger === 'object') return icerikOzeti(deger);
  if (TARIH_ALANLARI.includes(anahtar)) return tarihBicimle(deger);
  return String(deger);
};

const alanlaraCevir = veri =>
  Object.entries(veri || {})
    .filter(([, d]) => d !== undefined)
    .map(([anahtar, deger]) => ({
      anahtar,
      etiket: alanEtiketi(anahtar),
      deger: alanDegeri(anahtar, deger),
    }));

// --- Pikseller ------------------------------------------------------------
const pikseller = ref([]);
const pikselYukleniyor = ref(false);
const pikselHatasi = ref('');

const pikselSutunlari = computed(() => [
  { anahtar: 'name', baslik: t('META_ADS.AYARLAR.TAKIP.PIKSEL_ADI') },
  { anahtar: 'id', baslik: t('META_ADS.AYARLAR.TAKIP.KIMLIK') },
  {
    anahtar: 'last_fired_time',
    baslik: t('META_ADS.AYARLAR.TAKIP.SON_OLAY'),
    // Ham damga yerine yerel tarih; "Son olay" sutunu bir bakista okunsun.
    bicim: (deger, satir) => {
      const ham = deger || satir.last_fired_at;
      return ham ? tarihBicimle(ham) : '—';
    },
  },
]);

const pikselleriYukle = async () => {
  if (!props.hesap) return;
  pikselYukleniyor.value = true;
  pikselHatasi.value = '';
  try {
    const sonuc = await aracCalistir(hesapKimligi.value, 'ads_get_pixels', {
      account_id: props.hesap,
    });
    pikseller.value = diziyeCevir(sonuc);
  } catch (e) {
    pikselHatasi.value = e.response?.data?.error || e.message;
  } finally {
    pikselYukleniyor.value = false;
  }
};

// --- Veri kumesi kalitesi -------------------------------------------------
const kaliteBasligi = ref('');
const kaliteOzeti = ref('');
const kaliteAlanlari = ref([]);
const kaliteYukleniyor = ref(false);
const kaliteHatasi = ref('');

// Meta tarafinda piksel ile veri kumesi (dataset) ayni kimligi tasir.
const kaliteGetir = async piksel => {
  kaliteBasligi.value = piksel.name || piksel.id || '';
  kaliteYukleniyor.value = true;
  kaliteHatasi.value = '';
  kaliteAlanlari.value = [];
  kaliteOzeti.value = '';
  try {
    const sonuc = await aracCalistir(hesapKimligi.value, 'ads_get_dataset_quality', {
      dataset_id: piksel.id,
    });
    kaliteOzeti.value = sonuc.ozet || '';
    const veri = Array.isArray(sonuc.veri) ? sonuc.veri[0] : sonuc.veri;
    kaliteAlanlari.value = alanlaraCevir(veri);
  } catch (e) {
    kaliteHatasi.value = e.response?.data?.error || e.message;
  } finally {
    kaliteYukleniyor.value = false;
  }
};

// --- URL etiketleri -------------------------------------------------------
const reklamKimligi = ref('');
const urlEtiketleri = ref('');
const etiketKaydediliyor = ref(false);
const etiketHatasi = ref('');

const etiketleriGuncelle = async () => {
  if (!reklamKimligi.value.trim()) return;
  if (!window.confirm(t('META_ADS.AYARLAR.TAKIP.ETIKET_ONAYI'))) return;

  etiketKaydediliyor.value = true;
  etiketHatasi.value = '';
  try {
    await aracCalistir(
      hesapKimligi.value,
      'ads_update_ad_url_tags',
      { ad_id: reklamKimligi.value.trim(), url_tags: urlEtiketleri.value.trim() },
      { onaylandi: true }
    );
    useAlert(t('META_ADS.AYARLAR.TAKIP.ETIKET_GUNCELLENDI'));
  } catch (e) {
    etiketHatasi.value = e.response?.data?.error || e.message;
  } finally {
    etiketKaydediliyor.value = false;
  }
};

watch(() => props.hesap, pikselleriYukle);
onMounted(pikselleriYukle);
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <p v-if="!hesap" class="text-sm text-n-slate-11">
      {{ t('META_ADS.AYARLAR.TAKIP.HESAP_SEC') }}
    </p>

    <template v-else>
      <!-- Pikseller -->
      <section class="border rounded-lg border-n-weak">
        <header class="flex items-center gap-3 px-4 py-3 border-b border-n-weak">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('META_ADS.AYARLAR.TAKIP.PIKSELLER') }}
          </h2>
          <span class="grow" />
          <Button
            variant="faded"
            size="sm"
            icon="i-lucide-refresh-cw"
            :label="t('META_ADS.AYARLAR.TAKIP.YENILE')"
            @click="pikselleriYukle"
          />
        </header>

        <p v-if="pikselHatasi" class="px-4 py-3 text-sm text-n-ruby-11">{{ pikselHatasi }}</p>

        <AracTablosu
          :satirlar="pikseller"
          :sutunlar="pikselSutunlari"
          :yukleniyor="pikselYukleniyor"
          :bos-metin="t('META_ADS.AYARLAR.TAKIP.PIKSEL_YOK')"
        >
          <template #eylemler="{ satir }">
            <Button
              variant="faded"
              size="sm"
              icon="i-lucide-activity"
              :label="t('META_ADS.AYARLAR.TAKIP.VERI_KALITESI')"
              @click="kaliteGetir(satir)"
            />
          </template>
        </AracTablosu>
      </section>

      <!-- Veri kumesi kalitesi -->
      <section v-if="kaliteBasligi" class="border rounded-lg border-n-weak">
        <header class="px-4 py-3 border-b border-n-weak">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('META_ADS.AYARLAR.TAKIP.KALITE_BASLIGI', { ad: kaliteBasligi }) }}
          </h2>
        </header>

        <p v-if="kaliteHatasi" class="px-4 py-3 text-sm text-n-ruby-11">{{ kaliteHatasi }}</p>

        <p v-if="kaliteYukleniyor" class="px-4 py-6 text-sm text-n-slate-11">
          {{ t('META_ADS.AYARLAR.TAKIP.YUKLENIYOR') }}
        </p>

        <template v-else>
          <p v-if="kaliteOzeti" class="px-4 pt-3 text-sm whitespace-pre-line text-n-slate-11">
            {{ kaliteOzeti }}
          </p>

          <!-- Olcum varsa anahtar/deger satirlari, yoksa bos kutu yerine ne
               yapilmasi gerektigini anlatan metin gosteriliyor. -->
          <dl v-if="kaliteAlanlari.length" class="grid grid-cols-2 gap-x-6 gap-y-2 px-4 py-4">
            <div
              v-for="alan in kaliteAlanlari"
              :key="alan.anahtar"
              class="grid grid-cols-[auto_1fr] gap-x-4 text-sm"
            >
              <dt class="text-n-slate-11">{{ alan.etiket }}</dt>
              <dd class="break-words text-n-slate-12">{{ alan.deger }}</dd>
            </div>
          </dl>
          <p
            v-else-if="!kaliteOzeti && !kaliteHatasi"
            class="px-4 py-6 text-sm text-n-slate-11"
          >
            {{ t('META_ADS.AYARLAR.TAKIP.KALITE_YOK') }}
          </p>
        </template>
      </section>

      <!-- URL etiketleri -->
      <section class="border rounded-lg border-n-weak">
        <header class="px-4 py-3 border-b border-n-weak">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('META_ADS.AYARLAR.TAKIP.URL_ETIKETLERI') }}
          </h2>
        </header>

        <p v-if="etiketHatasi" class="px-4 py-3 text-sm text-n-ruby-11">{{ etiketHatasi }}</p>

        <div class="flex flex-wrap items-end gap-3 px-4 py-4">
          <label class="flex flex-col gap-1 text-xs text-n-slate-11">
            {{ t('META_ADS.AYARLAR.TAKIP.REKLAM_KIMLIGI') }}
            <input v-model="reklamKimligi" type="text" class="!mb-0 w-56" />
          </label>
          <label class="flex flex-col gap-1 text-xs text-n-slate-11 grow">
            {{ t('META_ADS.AYARLAR.TAKIP.ETIKET_DIZESI') }}
            <input
              v-model="urlEtiketleri"
              type="text"
              class="!mb-0 min-w-[320px]"
              placeholder="utm_source=facebook&utm_medium=cpc"
            />
          </label>
          <Button
            variant="solid"
            size="sm"
            :label="t('META_ADS.AYARLAR.TAKIP.ETIKETI_GUNCELLE')"
            :disabled="etiketKaydediliyor || !reklamKimligi"
            @click="etiketleriGuncelle"
          />
        </div>

        <p class="px-4 pb-4 text-xs text-n-slate-11">
          {{ t('META_ADS.AYARLAR.TAKIP.ETIKET_ACIKLAMA') }}
        </p>
      </section>
    </template>
  </div>
</template>
